import { Suspense } from 'react';



import { DefaultImageComponent, PDPCardCompact } from '@r123/pdp-card';
import { useLocale } from 'next-intl';
import dynamic from 'next/dynamic';



import HeadSRPCard from '@/app/[lang]/(search)/_components/SRPCard/HeadSRPCard/HeadSRPCard';
import BuildingInfo from '@/app/[lang]/(search)/_components/SRPCard/PropertyInfo/BuildingInfo';
import LocationInfo from '@/app/[lang]/(search)/_components/SRPCard/PropertyInfo/LocationInfo';
import PriceInfo from '@/app/[lang]/(search)/_components/SRPCard/PropertyInfo/PriceInfo';
import PropertyInfoChips from '@/app/[lang]/(search)/_components/SRPCard/PropertyInfo/PropertyInfoChips';
import TagPropertyTooltipContent from '@/app/[lang]/(search)/aset-bank/_components/TagPropertyTooltip/TagPropertyTooltipContent';
import { generatePriceDropNPL } from '@/app/[lang]/_helper/NPLHelper';
import ChipNPL from '@/app/_components/Chip/ChipNPL';
import { cn } from '@/app/_helper/tailwind';
import { getPropertyUrl } from '@/app/_helper/url';
import { PreferredCurrency, Property } from '@/modules/core/gql/generated';
import { useTooltip } from '@/modules/dialog/tooltips/context';
import { getGenericCacheUrl } from '@/modules/generic-cache';



import useRecommendationSectionSegment from '../_hooks/useRecommendationSectionSegment';


const PDPSwiper = dynamic(
    () =>
        import('@r123/pdp-card/dist/components/swiper').then(
            (module) => module.PDPSwiper,
        ),
    {
        ssr: false,
    },
);

const SpecialOfferRecommendationCard = ({
    property,
    position,
    preferredCurrency,
    className,
}: {
    property: Property;
    position: number;
    preferredCurrency?: PreferredCurrency | undefined;
    className?: string;
}) => {
    const {
        action: { showTooltip },
    } = useTooltip();

    const locale = useLocale();

    const {
        ref: { observe },
        action: { onClickCard },
    } = useRecommendationSectionSegment({
        property,
        index: position,
        threshold: 0.6,
    });

    const isNpl = property.specialFlag?.isNpl;
    const priceDropNPL = isNpl
        ? generatePriceDropNPL({
              auction: property.auction,
              offer: property.price.offer,
          })
        : undefined;

    if (property.specialFlag?.isPrimaryProject) {
        return (
            <div className='h-full [&>div]:h-full' ref={observe}>
                <PDPCardCompact
                    genericCacheUrl={getGenericCacheUrl() || ''}
                    strapiBaseUrl={process.env.NEXT_PUBLIC_STRAPI_API_URL || ''}
                    onClick={() => {
                        onClickCard();
                    }}
                    property={property}
                    lang={locale as 'id' | 'en'}
                    mediaComponent={
                        <Suspense
                            fallback={
                                <DefaultImageComponent
                                    className='aspect-[3/2] w-full md:h-auto h-auto'
                                    property={property}
                                />
                            }
                        >
                            <PDPSwiper
                                mediaCountClassName='md:bottom-0'
                                defaulImageClassName='aspect-[3/2] w-full md:h-auto h-auto'
                                className='[&_.swiper-pagination]:!bottom-4 aspect-[3/2] w-full md:h-auto h-auto'
                                property={property}
                            />
                        </Suspense>
                    }
                />
            </div>
        );
    }
    return (
        <div
            ref={observe}
            className={cn('bg-white relative w-[300px] shadow-[0px_2px_8px_0px_rgba(40,41,61,.08),0px_20px_15px_0px_rgba(96,97,112,.24)] rounded-2xl', className)}
            onClick={() => onClickCard()}
        >
            <div className='overflow-hidden rounded-t-2xl'>
                {property && (
                    <HeadSRPCard
                        {...(property as Property)}
                        propertyPartner={property.propertyPartner || undefined}
                        unregisterProject={
                            property.unregisterProject || undefined
                        }
                        uuid={property.uuid}
                        medias={property.medias}
                        priceDrop={
                            priceDropNPL ? priceDropNPL : property.priceDrop
                        }
                        title={property.title}
                        ratio='3/2'
                        pageType='LDP'
                        isNpl={isNpl}
                        preferredCurrency={preferredCurrency}
                        smallCard
                        showSave
                    />
                )}
            </div>
            <div className='p-4 flex flex-col gap-y-2 h-[198px]' data-name="content">
                <div
                    className={cn(
                        'flex justify-between items-center bg-[linear-gradient(90deg,rgba(193,214,255,0.80)_0%,#F3F7FF_112.86%)] -mx-4 -mt-4 py-1.5',
                        {
                            'px-3': !property.specialFlag?.isNpl,
                        },
                    )}
                >
                    <PropertyInfoChips
                        propertyType={property.propertyType}
                        specialFlag={property.specialFlag}
                        priceType={property.priceType}
                        url={property.url}
                    >
                        {property.specialFlag?.isNpl && (
                            <ChipNPL
                                onClick={() => {
                                    showTooltip({
                                        show: true,
                                        title: 'Tag Properti',
                                        content: <TagPropertyTooltipContent />,
                                    });
                                }}
                            />
                        )}
                    </PropertyInfoChips>
                </div>
                <PriceInfo
                    size='small'
                    price={property.price.display}
                    installment={undefined}
                    priceDrop={priceDropNPL ? priceDropNPL : property.priceDrop}
                    preferredCurrency={preferredCurrency}
                    marketValuation={property.price?.marketValuation}
                    offer={property?.price?.offer}
                    isNpl={isNpl}
                    enableCompactSavingLabel
                />
                <LocationInfo
                    title={property.title}
                    location={property.location.text}
                    url={getPropertyUrl({
                        slug: property?.slug || '',
                        url: property?.url || '',
                    })}
                    linkCoverParent
                />
                <BuildingInfo
                    bedroom={property.attributes?.bedrooms?.formattedValue}
                    bathroom={property.attributes?.bathrooms?.formattedValue}
                    carports={property.attributes?.carports?.formattedValue}
                    buildingSize={
                        property.attributes?.buildingSize?.formattedValue
                    }
                    landSize={property.attributes?.landSize?.formattedValue}
                />
            </div>
        </div>
    );
};

export default SpecialOfferRecommendationCard;